import React from 'react';
import {Banner, Icon, Text} from '@albert-io/atomic';
import LicenseStatusCard from './LicenseStatusCard';
import type {ClassroomV1} from '@albert-io/models';

interface LicenseStatusRadioGroupProps extends PropsWithClassNameOptional {
  isUserForClassLicensed: boolean;
  isLicensed: boolean;
  setIsLicensed: (isLicensed: boolean) => void;
  isSchoolLicenseInactive?: boolean;
  disabled?: boolean;
  className?: string;
  classroom?: ClassroomV1;
}

const LicenseStatusRadioGroup = ({
  isUserForClassLicensed,
  isLicensed,
  setIsLicensed,
  isSchoolLicenseInactive = false,
  disabled,
  className,
  classroom
}: LicenseStatusRadioGroupProps) => {
  const initialLicensedState = classroom?.apply_students_to_school_license;

  const cantEnableLicense =
    !initialLicensedState && !classroom?.getMeta()?.isCanEnableLicense?.() && classroom;
  const cantDisableLicense =
    initialLicensedState && !classroom?.getMeta()?.isCanDisableLicense?.() && classroom;

  return (
    <div
      className={`gap-x1 flex flex-col ${className}`}
      // The read-only state renders no radios, and `radiogroup` requires them.
      role={isUserForClassLicensed ? 'radiogroup' : 'group'}
      aria-label='License status'
    >
      <Text size='m'>Class License Status</Text>
      {isSchoolLicenseInactive && (
        <Banner
          alignContent='center'
          type='warning'
          data-testid='license-status__inactive-school-banner'
        >
          <Banner.Icon icon='exclamation-triangle' iconStyle='regular' />
          {/* role=status: changing the School select inserts this mid-form. */}
          <Text color='inherit' role='status'>
            The school license associated with this account is inactive.
          </Text>
        </Banner>
      )}
      {isUserForClassLicensed ? (
        <>
          <LicenseStatusCard
            isLicensed
            selected={isLicensed}
            onClick={() => setIsLicensed(true)}
            warning={
              cantEnableLicense
                ? 'Requires additional license seats. Reach out to your admin.'
                : undefined
            }
            disabled={disabled}
          />
          <LicenseStatusCard
            isLicensed={false}
            selected={!isLicensed}
            onClick={() => setIsLicensed(false)}
            warning={
              cantDisableLicense
                ? 'License access cannot be changed after a student places a guess.'
                : undefined
            }
            disabled={disabled}
          />
        </>
      ) : (
        <>
          <LicenseStatusCard isLicensed={initialLicensedState} readonly />
          <div className='flex items-center'>
            <Icon icon='info-circle' iconStyle='light' color='secondary' className='mr-x0-half' />
            <Text size='s' color='secondary' italic>
              Only licensed staff members can edit this setting.
            </Text>
          </div>
        </>
      )}
    </div>
  );
};

export default LicenseStatusRadioGroup;
